import React, { useState, useEffect, useRef } from "react";

const DATE_FORMATS = [
  { value: "DD/MM/YYYY", label: "25/10/2023" },
  { value: "DD-MM-YYYY", label: "25-10-2023" },
  { value: "YYYY-MM-DD", label: "2023-10-25" },
  { value: "DD/MM/YY", label: "25/10/23" },
  { value: "DD MMMM YYYY", label: "25 octobre 2023" },
  { value: "LONG_FR", label: "lundi 25 octobre 2023" },
  { value: "MMMM YYYY", label: "octobre 2023" },
  { value: "DD/MM", label: "25/10" },
];

const FORMULA_FUNCTIONS = [
  {
    name: "SUM",
    syntax: "SUM(valeur1, valeur2, ...)",
    description: "Additionne plusieurs valeurs numériques",
    example: "SUM(prix_ht, tva) -> prix_ht + tva",
  },
  {
    name: "ROUND",
    syntax: "ROUND(valeur, décimales)",
    description: "Arrondit un nombre à un nombre donné de décimales",
    example: "ROUND(montant, 2) -> arrondi à 2 décimales",
  },
  {
    name: "CONCAT",
    syntax: "CONCAT(texte1, texte2, ...)",
    description: "Concatène plusieurs chaînes de texte",
    example: "CONCAT(nom, ' ', prenom) -> 'Dupont Jean'",
  },
  {
    name: "IF",
    syntax: "IF(condition, valeur_si_vrai, valeur_si_faux)",
    description: "Condition logique (équivalent Excel)",
    example: "IF(age >= 18, 'Majeur', 'Mineur')",
  },
  {
    name: "MAX",
    syntax: "MAX(valeur1, valeur2, ...)",
    description: "Retourne la valeur maximale",
    example: "MAX(note1, note2, note3)",
  },
  {
    name: "MIN",
    syntax: "MIN(valeur1, valeur2, ...)",
    description: "Retourne la valeur minimale",
    example: "MIN(prix1, prix2, prix3)",
  },
  {
    name: "UPPER",
    syntax: "UPPER(texte)",
    description: "Convertit en majuscules",
    example: "UPPER(nom) -> 'DUPONT'",
  },
  {
    name: "LOWER",
    syntax: "LOWER(texte)",
    description: "Convertit en minuscules",
    example: "LOWER(nom) -> 'dupont'",
  },
  {
    name: "LEN",
    syntax: "LEN(texte)",
    description: "Retourne la longueur du texte",
    example: "LEN(description) -> nombre de caractères",
  },
  {
    name: "TODAY",
    syntax: "TODAY()",
    description: "Retourne la date du jour",
    example: "TODAY() -> date actuelle",
  },
  {
    name: "YEAR",
    syntax: "YEAR(date)",
    description: "Extrait l'année d'une date",
    example: "YEAR(date_naissance) -> 1990",
  },
  {
    name: "MONTH",
    syntax: "MONTH(date)",
    description: "Extrait le mois d'une date (1-12)",
    example: "MONTH(date_facture) -> 5",
  },
  {
    name: "DAYS",
    syntax: "DAYS(date1, date2)",
    description: "Nombre de jours entre deux dates",
    example: "DAYS(date_debut, date_fin)",
  },
  {
    name: "NUMTOTEXT",
    syntax: "NUMTOTEXT(nombre)",
    description: "Convertit un nombre en texte français",
    example: "NUMTOTEXT(123.45) -> 'cent vingt-trois virgule quarante-cinq'",
  },
  {
    name: "DATETOTEXT",
    syntax: "DATETOTEXT(date, format)",
    description: "Convertit une date en texte avec choix du format",
    example: "DATETOTEXT(date, 'DD/MM/YYYY') -> '25/10/2023'",
    hasFormatSelector: true,
  },
  {
    name: "EQUALS",
    syntax: "EQUALS(texte1, texte2)",
    description: "Compare deux textes (insensible à la casse)",
    example: "EQUALS(statut, 'ACTIF')",
  },
  {
    name: "CONTAINS",
    syntax: "CONTAINS(texte, sous_texte)",
    description: "Vérifie si un texte contient un sous-texte",
    example: "CONTAINS(description, 'urgent')",
  },
  {
    name: "IN",
    syntax: "IN(valeur, 'option1,option2,option3')",
    description: "Vérifie si une valeur est dans une liste CSV",
    example: "IN(categorie, 'A,B,C')",
  },
  {
    name: "IFNULL",
    syntax: "IFNULL(valeur, valeur_defaut)",
    description: "Retourne valeur_defaut si valeur est nulle",
    example: "IFNULL(remise, 0)",
  },
  {
    name: "WHEN",
    syntax: "WHEN(valeur, 'clé1', résultat1, 'clé2', résultat2, ..., défaut)",
    description: "Sélecteur à paires clé-valeur",
    example: "WHEN(civilite, 'M', 'Monsieur', 'Mme', 'Madame', 'Neutre')",
  },
  {
    name: "PICKFROMCSV",
    syntax: "PICKFROMCSV(valeur, 'clé1,clé2,clé3', 'val1,val2,val3')",
    description: "Sélectionne une valeur depuis deux listes CSV",
    example: "PICKFROMCSV(etat, 'C,M,D', 'Célibataire,Marié,Divorcé')",
  },
];

const FormulaHelper = ({ isOpen, onClose, onInsertFunction, fields = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [fieldSearchTerm, setFieldSearchTerm] = useState("");
  const [selectedFormat, setSelectedFormat] = useState(DATE_FORMATS[0].value);
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState("functions");
  const tabsWrapRef = useRef(null);
  const indicatorRef = useRef(null);

  const TAB_ORDER = ["functions", "fields", "help"];
  const currentIndex = TAB_ORDER.indexOf(activeTab);

  function moveIndicator() {
    const wrap = tabsWrapRef.current;
    const ind = indicatorRef.current;
    if (!wrap || !ind) return;
    const activeBtn = wrap.querySelector(".formula-tab.active");
    if (!activeBtn) return;
    const btnRect = activeBtn.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    ind.style.width = `${btnRect.width}px`;
    ind.style.transform = `translateX(${btnRect.left - wrapRect.left}px)`;
  }

  function onTabsKeyDown(e) {
    const key = e.key;
    if (["ArrowRight", "ArrowLeft", "Home", "End"].includes(key)) {
      e.preventDefault();
      let idx = currentIndex;
      if (key === "ArrowRight") idx = (currentIndex + 1) % TAB_ORDER.length;
      if (key === "ArrowLeft") idx = (currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length;
      if (key === "Home") idx = 0;
      if (key === "End") idx = TAB_ORDER.length - 1;
      setActiveTab(TAB_ORDER[idx]);
      setTimeout(() => {
        const btn = tabsWrapRef.current?.querySelector(`#tab-${TAB_ORDER[idx]}`);
        btn?.focus();
      }, 0);
    }
    if (key === "Enter" || key === " ") {
      e.preventDefault();
    }
  }

  // Gestion du focus et animations
  useEffect(() => {
    if (isOpen) {
      // Focus sur le champ de recherche des fonctions
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);

      // Empêcher le scroll du body
      document.body.style.overflow = 'hidden';
    } else {
      // Restaurer le scroll
      document.body.style.overflow = 'unset';
    }

    // Cleanup
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Gestion de la fermeture avec Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    moveIndicator();
  }, [activeTab, isOpen]);

  useEffect(() => {
    const onResize = () => moveIndicator();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const filteredFunctions = FORMULA_FUNCTIONS.filter(
    (func) =>
      func.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      func.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFields = fields.filter(
    (field) =>
      field.key.toLowerCase().includes(fieldSearchTerm.toLowerCase()) ||
      (field.label || "").toLowerCase().includes(fieldSearchTerm.toLowerCase())
  );

  const handleInsertFunction = (func) => {
    if (func.hasFormatSelector && func.name === "DATETOTEXT") {
      const syntax = `DATETOTEXT(date, '${selectedFormat}')`;
      onInsertFunction(syntax);
    } else {
      onInsertFunction(func.syntax);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="formula-helper-overlay show" onClick={onClose}>
      <div
        className="formula-helper-modal"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="formula-helper-header">
          <h3>
            <i className="fas fa-calculator"></i> Aide Formules
          </h3>
          <button className="formula-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="formula-helper-content">
          <div
            className="formula-tabs"
            role="tablist"
            aria-label="Aide Formules"
            ref={tabsWrapRef}
            onKeyDown={onTabsKeyDown}
          >
            <button
              id="tab-functions"
              role="tab"
              aria-controls="panel-functions"
              aria-selected={activeTab === "functions"}
              tabIndex={activeTab === "functions" ? 0 : -1}
              className={`formula-tab ${activeTab === "functions" ? "active" : ""}`}
              onClick={() => setActiveTab("functions")}
            >
              <i className="fas fa-function"></i> Fonctions
            </button>
            <button
              id="tab-fields"
              role="tab"
              aria-controls="panel-fields"
              aria-selected={activeTab === "fields"}
              tabIndex={activeTab === "fields" ? 0 : -1}
              className={`formula-tab ${activeTab === "fields" ? "active" : ""}`}
              onClick={() => setActiveTab("fields")}
            >
              <i className="fas fa-tags"></i> Insérer un champ
            </button>
            <button
              id="tab-help"
              role="tab"
              aria-controls="panel-help"
              aria-selected={activeTab === "help"}
              tabIndex={activeTab === "help" ? 0 : -1}
              className={`formula-tab ${activeTab === "help" ? "active" : ""}`}
              onClick={() => setActiveTab("help")}
            >
              <i className="fas fa-info-circle"></i> Aide
            </button>

            {/* Indicateur glissant */}
            <span className="fh-tab-indicator" ref={indicatorRef} aria-hidden="true" />
          </div>

          {activeTab === "functions" && (
            <div className="formula-section">
              <h4><i className="fas fa-function"></i> Fonctions</h4>
              <div
                id="panel-functions"
                role="tabpanel"
                aria-labelledby="tab-functions"
                className="formula-tab-panel"
              >
                <div className="formula-helper-search">
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Rechercher une fonction..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="formula-functions-grid">
                  {filteredFunctions.map((func) => (
                    <div key={func.name} className="formula-function-card">
                      <div className="function-card-header">
                        <strong className="function-name">{func.name}</strong>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleInsertFunction(func)}
                          title="Insérer cette fonction"
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                      <div className="function-details">
                        <div className="function-syntax">
                          <code>{func.syntax}</code>
                        </div>
                        <div className="function-description">{func.description}</div>
                        <div className="function-example">
                          <strong>Exemple:</strong> {func.example}
                        </div>
                      </div>
                      {func.hasFormatSelector && func.name === "DATETOTEXT" && (
                        <div className="format-selector">
                          <label>Format de date:</label>
                          <select
                            value={selectedFormat}
                            onChange={(e) => setSelectedFormat(e.target.value)}
                            className="form-select"
                          >
                            {DATE_FORMATS.map((format) => (
                              <option key={format.value} value={format.value}>
                                {format.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "fields" && (
            <div className="formula-section">
              <h4><i className="fas fa-tags"></i> Insérer un champ</h4>
              <div
                id="panel-fields"
                role="tabpanel"
                aria-labelledby="tab-fields"
                className="formula-tab-panel"
              >
                <div className="formula-helper-search">
                  <input
                    type="text"
                    placeholder="Rechercher un champ..."
                    value={fieldSearchTerm}
                    onChange={(e) => setFieldSearchTerm(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="formula-fields-grid">
                  {filteredFields.map((field) => (
                    <div key={field.id} className="formula-field-card">
                      <div className="field-card-info">
                        <strong className="field-key">{field.key}</strong>
                        {field.label && field.label !== field.key && (
                          <span className="field-label"> - {field.label}</span>
                        )}
                        <span className="field-type">{field.type}</span>
                      </div>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => onInsertFunction(field.key)}
                        title={`Insérer le champ ${field.key}`}
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === "help" && (
            <div className="formula-section">
              <h4><i className="fas fa-info-circle"></i> Aide</h4>
              <div
                id="panel-help"
                role="tabpanel"
                aria-labelledby="tab-help"
                className="formula-tab-panel"
              >
                <div className="formula-helper-intro">
                  <div className="help-tips">
                    <div className="help-tip">
                      <i className="fas fa-lightbulb"></i>
                      <strong>Variables disponibles :</strong> Utilisez les clés des autres champs comme variables (ex: nom_client, prix_ht).
                    </div>
                    <div className="help-tip">
                      <i className="fas fa-list"></i>
                      <strong>Variables tableau :</strong> Pour les champs répétables, utilisez nom_tableau.champ (ex: lignes.montant).
                    </div>
                    <div className="help-tip">
                      <i className="fas fa-quote-left"></i>
                      <strong>Chaînes de texte :</strong> Entourez les textes de guillemets simples (ex: 'texte').
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormulaHelper;
